import { BrandKit, defaultBrandKit } from "@redotech/redo-model/brand-kit";
import {
  ElementVisibility,
  MarketingForgeElementType,
} from "@redotech/redo-model/content-builder/element-schema";
import { LayoutType } from "@redotech/redo-model/signup-form";
import {
  DesktopStepConfig,
  MobileStepConfig,
  SpotlightForm,
} from "@redotech/redo-model/spotlight-schema";
import { memo, NamedExoticComponent, useMemo, useState } from "react";
import {
  ButtonSize,
  RedoButtonCloseX,
} from "../../arbiter-components/redo-button-close-x";
import { Flex } from "../../flex";
import { SignupFormDiscountCodeDisplay } from "../form-elements/discount-code";
import { FormButton } from "../form-elements/form-button";
import { FormColumn } from "../form-elements/form-column";
import { FormContainer } from "../form-elements/form-container";
import { FormImage } from "../form-elements/form-image";
import { FormProduct } from "../form-elements/form-product";
import { FormElementProps } from "../form-elements/index";
import { Input } from "../form-elements/input";
import { RichText } from "../form-elements/rich-text";
import { TextToSubscribeButton } from "../form-elements/text-to-subscribe-button";
import { TextToSubscribeDescription } from "../form-elements/text-to-subscribe-description";
import { SpotlightFontLoader } from "./spotlight-font-loader";

const elementComponents: Record<
  MarketingForgeElementType,
  NamedExoticComponent<FormElementProps<any>>
> = {
  [MarketingForgeElementType.INPUT]: Input,
  [MarketingForgeElementType.RICH_TEXT]: RichText,
  [MarketingForgeElementType.BUTTON]: FormButton,
  [MarketingForgeElementType.IMAGE]: FormImage,
  [MarketingForgeElementType.PRODUCT]: FormProduct,
  [MarketingForgeElementType.COLUMN]: FormColumn,
  [MarketingForgeElementType.CONTAINER]: FormContainer,
  [MarketingForgeElementType.DISCOUNT]: SignupFormDiscountCodeDisplay,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON]: TextToSubscribeButton,
  [MarketingForgeElementType.TEXT_TO_SUBSCRIBE_DESCRIPTION]:
    TextToSubscribeDescription,
};

export interface SpotlightFormContentProps {
  spotlight: SpotlightForm;
  currentStep: number;
  brandKit?: BrandKit;
  anonymousId?: string;
  merchantPhoneNumber?: string;
  onClose?: () => void;
  isMobile: boolean;
  config: MobileStepConfig | DesktopStepConfig;
  onSubmitStep: (
    stepIndex: number,
    formData: Record<string, string>,
  ) => Promise<void>;
  resolvedDiscounts?: Record<string, string>;
  onCheckoutAction?: () => Promise<void>;
  buttonSubtext?: string;
}

export const SpotlightFormContent = memo(function SpotlightFormContent({
  spotlight,
  brandKit,
  currentStep,
  anonymousId,
  merchantPhoneNumber,
  onClose,
  config,
  isMobile,
  onSubmitStep,
  resolvedDiscounts,
  onCheckoutAction,
  buttonSubtext,
}: SpotlightFormContentProps) {
  const [submitting, setSubmitting] = useState(false);
  const step = spotlight.steps[currentStep];

  const closeActionContext = useMemo(
    () => ({ resolvedDiscounts, onCheckoutAction, buttonSubtext }),
    [resolvedDiscounts, onCheckoutAction, buttonSubtext],
  );

  if (!step) {
    return null;
  }

  const advancedSettings = config.advancedSettings;
  const backgroundColor = advancedSettings.background;
  const padding = advancedSettings.padding;
  const cornerRadius =
    "cornerRadius" in advancedSettings ? advancedSettings.cornerRadius : 0;

  return (
    <Flex
      dir="column"
      gap="none"
      grow="1"
      style={{
        backgroundColor,
        padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
        position: "relative",
        borderRadius: cornerRadius,
        overflow: "hidden",
      }}
    >
      <SpotlightFontLoader
        customFontFamilies={brandKit?.customFontFamilies}
        spotlight={spotlight}
      />
      {onClose && (
        <Flex style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
          <RedoButtonCloseX
            onclick={onClose}
            onDarkBackground={false}
            size={ButtonSize.SMALL}
          />
        </Flex>
      )}
      <form
        onSubmit={async (e) => {
          setSubmitting(true);
          e.preventDefault();
          const formData = new FormData(e.currentTarget);
          const transformedData: Record<string, string> = {};
          for (const [key, value] of formData.entries()) {
            transformedData[key] = value as string;
          }
          try {
            await onSubmitStep(currentStep, transformedData);
            setSubmitting(false);
          } catch (err) {
            console.error(err);
            setSubmitting(false);
          }
        }}
        style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
      >
        {step.elements
          .filter((element) => {
            const visibility = element.visibility ?? ElementVisibility.ALL;
            if (visibility === ElementVisibility.DESKTOP_ONLY && isMobile) {
              return false;
            }
            if (visibility === ElementVisibility.MOBILE_ONLY && !isMobile) {
              return false;
            }
            return true;
          })
          .map((element, index) => {
            const ElementComponent = elementComponents[element.type];
            return (
              <ElementComponent
                anonymousId={anonymousId}
                backgroundColor={backgroundColor}
                brandKit={brandKit ?? defaultBrandKit}
                brandKitOverrides={undefined}
                closeActionContext={closeActionContext}
                element={element}
                isMobile={isMobile}
                key={index}
                layoutType={LayoutType.POPUP}
                merchantPhoneNumber={merchantPhoneNumber}
                onClose={onClose}
                submitting={submitting}
              />
            );
          })}
      </form>
    </Flex>
  );
});
